import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Cookie } from 'lucide-react';
import { theme } from '../../design-system';
import { hasConsentChoice, acceptAll, declineAll } from '../../utils/cookieConsent';

const Overlay = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from {
      transform: translateY(100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const Banner = styled.div`
  background: ${theme.colors.background.primary};
  border-top: 3px solid ${theme.colors.primary[500]};
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
  padding: ${theme.spacing[6]};

  @media (max-width: 768px) {
    padding: ${theme.spacing[4]};
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[6]};

  @media (max-width: 768px) {
    flex-direction: column;
    gap: ${theme.spacing[4]};
  }
`;

const IconWrapper = styled.div`
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  background: ${theme.colors.primary[100]};
  border-radius: ${theme.radii.xl};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.primary[600]};

  @media (max-width: 768px) {
    display: none;
  }
`;

const Content = styled.div`
  flex: 1;
`;

const Title = styled.h3`
  font-size: ${theme.fontSizes.lg};
  font-weight: ${theme.fontWeights.semibold};
  color: ${theme.colors.text.primary};
  margin: 0 0 ${theme.spacing[2]};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  svg {
    display: none;
    @media (max-width: 768px) {
      display: block;
      color: ${theme.colors.primary[500]};
    }
  }
`;

const Description = styled.p`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.secondary};
  line-height: ${theme.lineHeights.relaxed};
  margin: 0;

  a {
    color: ${theme.colors.primary[600]};
    text-decoration: underline;
    font-weight: ${theme.fontWeights.medium};

    &:hover {
      color: ${theme.colors.primary[700]};
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  flex-shrink: 0;

  @media (max-width: 768px) {
    flex-direction: row;
    width: 100%;
  }
`;

const Button = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  border-radius: ${theme.radii.lg};
  font-size: ${theme.fontSizes.sm};
  font-weight: ${theme.fontWeights.semibold};
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  @media (max-width: 768px) {
    flex: 1;
    padding: ${theme.spacing[3]} ${theme.spacing[4]};
  }
`;

const AcceptButton = styled(Button)`
  background: ${theme.colors.primary[500]};
  color: white;
  border: none;

  &:hover {
    background: ${theme.colors.primary[600]};
    transform: translateY(-1px);
  }
`;

const DeclineButton = styled(Button)`
  background: transparent;
  color: ${theme.colors.text.secondary};
  border: 1px solid ${theme.colors.border.default};

  &:hover {
    background: ${theme.colors.neutral[50]};
    border-color: ${theme.colors.border.dark};
  }
`;

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if user has already made a consent choice
    if (!hasConsentChoice()) {
      // Small delay to prevent flash on page load
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAccept = () => {
    acceptAll();
    setIsVisible(false);
    // Reload to initialize tracking
    window.location.reload();
  };

  const handleDecline = () => {
    declineAll();
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <Overlay>
      <Banner>
        <Container>
          <IconWrapper>
            <Cookie size={24} />
          </IconWrapper>

          <Content>
            <Title>
              <Cookie size={20} />
              We Value Your Privacy
            </Title>
            <Description>
              We use cookies to improve your browsing experience, understand how you use our site,
              and show you relevant content. By clicking "Accept All", you consent to the use of cookies.
              You can manage your preferences or learn more in our{' '}
              <Link to="/privacy-policy">Privacy Policy</Link> and{' '}
              <Link to="/terms-of-service">Terms of Service</Link>.
            </Description>
          </Content>

          <ButtonGroup>
            <AcceptButton onClick={handleAccept}>
              Accept All
            </AcceptButton>
            <DeclineButton onClick={handleDecline}>
              Decline Optional
            </DeclineButton>
          </ButtonGroup>
        </Container>
      </Banner>
    </Overlay>
  );
};

export default CookieConsent;
